import { GA_MEASUREMENT_ID } from '@/config/analytics';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Initialize Google Analytics
export const initializeGA = (): void => {
  if (typeof window === 'undefined') return;

  // Create dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  // Initialize gtag with timestamp
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_title: document.title,
    page_location: window.location.href,
  });
};

// Load Google Analytics script
export const loadGAScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Window is not available'));
      return;
    }

    // Check if script is already loaded
    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    
    script.onload = () => {
      initializeGA();
      resolve();
    };
    
    script.onerror = () => {
      reject(new Error('Failed to load Google Analytics script'));
    };

    document.head.appendChild(script);
  });
};

// Track page views
export const trackPageView = (path: string, title?: string): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
};

// Track custom events
export const trackEvent = (action: string, category: string, label?: string, value?: number): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

// Remove Google Analytics
export const removeGA = (): void => {
  if (typeof window === 'undefined') return;

  // Remove the script
  const script = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`);
  if (script) {
    script.remove();
  }

  // Clear gtag function
  if (window.gtag) {
    delete window.gtag;
  }

  // Clear dataLayer
  if (window.dataLayer) {
    window.dataLayer = [];
  }
};

// Get GA Measurement ID for configuration
export const getGAMeasurementId = (): string => {
  return GA_MEASUREMENT_ID;
};